import axios from "axios";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";

/* -------------------------------------------------------
   HELPER: Normalize Screen Data

   Converts string symbol IDs to numbers (no transformation)
------------------------------------------------------- */
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) {
    return screenData;
  }

  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      return typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
    });
  });
}

/* -------------------------------------------------------
   HELPER: Check if screen has regular symbols (non-coin, non-blank)

   Returns true if screen contains any regular symbols (4, 5, 6, 7, 8, 9, 10)
   This indicates reload happened right after trigger (before first respin)
------------------------------------------------------- */
function hasRegularSymbols(screenData) {
  if (!screenData || !Array.isArray(screenData)) {
    return false;
  }

  for (const reel of screenData) {
    if (!Array.isArray(reel)) continue;
    for (const symbolId of reel) {
      const normalizedSymbolId =
        typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      // Check if it's not a coin and not a blank (0)
      if (
        normalizedSymbolId !== 0 &&
        !COIN_SYMBOL_IDS.includes(normalizedSymbolId)
      ) {
        return true; // Found a regular symbol
      }
    }
  }
  return false; // Only coins and blanks
}

/* -------------------------------------------------------
   HELPER: Transform screen for Hold and Win rendering

   Replaces all non-coin symbols with 0 (blank)
   Used for trigger rounds and intro reloads where backend sends coins + regular symbols
------------------------------------------------------- */
function transformForHoldAndWin(normalizedReels) {
  return normalizedReels.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const normalizedSymbolId = typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      return COIN_SYMBOL_IDS.includes(normalizedSymbolId) ? normalizedSymbolId : 0;
    });
  });
}

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB

   Helper function to create inline Web Worker from code string.
   Uses Blob URL pattern consistent with other RGS providers.
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

/* -------------------------------------------------------
   INLINE spinWorker.js

   Worker Lifecycle Notes:
   - Worker instance will be created in spin() method
   - Worker can be reused for multiple spins
   - Worker termination handled by browser when page unloads
   - Memory management: single worker instance per provider instance
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature } = event.data;

    // Validate required fields
    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "PopiPlay: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      // CSRF header handling in worker: PopiPlay requires CSRF tokens for all API calls
      // CSRF header name and value are provided dynamically via window.__OPTIONS__
      // Header name is dynamic (e.g., "X-CSRF-Token") and value is a dynamic token
      // Both are included in the request headers using computed property name syntax
      // API URL structure: apiUrl includes play_token in path (same URL used for all spins)
      // Command parameter: "spin" for regular game, "respin" for feature game (determined by available_actions)
      const response = await axios.post(
        apiUrl, // API URL includes play_token in path (from window.__OPTIONS__.api)
        {
          command: command, // Dynamic command: "spin" or "respin" based on game state
          options: {
            bet: bet,
            ...(purchasedFeature ? { purchased_feature: purchasedFeature } : {})
          }
        },
        {
          headers: {
            [csrfTokenName]: csrfTokenValue, // Dynamic CSRF header: header name and value from window.__OPTIONS__
            "Content-Type": "application/json"
          }
        }
      );

      // Validate response structure
      if (!response.data) {
        postMessage({ error: "PopiPlay: Spin API call failed: Invalid response structure" });
        return;
      }

      // Check if response contains errors (API returns 200 OK with errors in body)
      if (response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        // Send error response
        postMessage({ error: response.data });
        return;
      }

      // Send raw API response - balance will be extracted in provider spin() method
      // Note: balance comes as {game: 0, wallet: 79461}, we extract wallet later
      postMessage(response.data);

    } catch (error) {
      // Send full error response if available, otherwise send error message
      if (error.response?.data?.errors) {
        // Send full error object with errors array
        postMessage({ error: error.response.data });
      } else {
        // Fallback to error message
        const errorMessage = "PopiPlay: Spin API call failed: " + error.message;
        postMessage({ error: errorMessage });
      }
    }
  };
`;

/**
 * RGS provider implementation
 *
 * Provides provider-specific implementation for game initialization,
 * spin execution, and simulation functionality.
 *
 * The class follows the established provider pattern structure,
 * with method signatures compatible with existing game code that expects token parameters.
 */
class PopiPlayRGS {
  /**
   * Constructor
   *
   * Initializes the API base URL from window.__OPTIONS__ if available,
   * otherwise falls back to an empty string for development/testing.
   *
   * window.__OPTIONS__ is server-embedded configuration provided by the server.
   * It contains: api, play_token, csrfTokenHeaderName, csrfTokenHeaderValue, identifier, currency
   */
  constructor() {
    // Extract API URL from window.__OPTIONS__ (server-embedded configuration)
    // API URL includes play_token in path
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null; // Lazy initialization in spin() method - worker created on first spin() call
    this.currentToken = null; // Token from previous spin (flow.round_id) - rotates with each spin
  }

  /**
   * Transform response by normalizing symbol IDs
   *
   * Screen contains visible symbols (typically 4 per reel).
   * The controller handles all sprite management programmatically,
   * so we just need to normalize string IDs to numbers.
   *
   * @param {Object} response - API response
   * @returns {Object} Response with normalized reels
   */
  transformReelsSymbols(response) {
    if (response.outcome && Array.isArray(response.outcome.screen)) {
      // Just normalize screen symbols from strings to numbers (no buffers needed)
      response.outcome.reels = response.outcome.screen.map((reel) => {
        if (!Array.isArray(reel)) {
          return [];
        }

        // Normalize symbols from strings to numbers
        return reel.map((symbolValue) => {
          if (typeof symbolValue === "string") {
            return parseInt(symbolValue, 10);
          }
          return symbolValue;
        });
      });

      // Also normalize the screen array for consistency
      response.outcome.screen = response.outcome.screen.map((reel) => {
        if (!Array.isArray(reel)) {
          return reel;
        }
        return reel.map((symbolValue) => {
          if (typeof symbolValue === "string") {
            return parseInt(symbolValue, 10);
          }
          return symbolValue;
        });
      });
    }
    return response;
  }

  /**
   * Get provider-specific response handler configuration
   *
   * @returns {Object} Handler configuration with patterns and propertyMap
   */
  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: (response) => false,
            extractFreeSpins: (round) => {
              return [];
            },
            extractTriggerRound: (round) => {
              return round;
            },
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) {
              return {};
            }

            const transformedOutcome = { ...outcome };

            if (Array.isArray(outcome.wins)) {
              transformedOutcome.wins = outcome.wins
                .map((winTuple) => {
                  if (!Array.isArray(winTuple) || winTuple.length < 4) {
                    return null;
                  }

                  const [, amount, positions, lineId] = winTuple;

                  if (!Array.isArray(positions)) {
                    return null;
                  }

                  const transformedPositions = positions.map(
                    (rowValue, reelIndex) => {
                      if (Array.isArray(rowValue) && rowValue.length >= 2) {
                        return [rowValue[0], rowValue[1]];
                      }
                      return [reelIndex, rowValue];
                    }
                  );

                  let symbol = null;
                  if (
                    Array.isArray(outcome.screen) &&
                    transformedPositions.length > 0
                  ) {
                    const firstPosition = transformedPositions[0];
                    if (
                      Array.isArray(firstPosition) &&
                      firstPosition.length >= 2
                    ) {
                      const [reelIdx, rowIdx] = firstPosition;
                      if (
                        outcome.screen[reelIdx] &&
                        Array.isArray(outcome.screen[reelIdx]) &&
                        outcome.screen[reelIdx][rowIdx] !== undefined
                      ) {
                        const symbolValue = outcome.screen[reelIdx][rowIdx];
                        symbol =
                          typeof symbolValue === "string"
                            ? parseInt(symbolValue, 10)
                            : symbolValue;
                      }
                    }
                  }

                  return {
                    id: lineId,
                    win: amount,
                    positions: transformedPositions,
                    symbol: symbol,
                  };
                })
                .filter((win) => win !== null);
            } else {
              transformedOutcome.wins = [];
            }

            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "outcome.win",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  /**
   * Initialize game session
   *
   * Reads configuration from window.__OPTIONS__ (server-embedded configuration),
   * makes POST request to init API endpoint, and returns normalized
   * game state matching standard format.
   *
   * @returns {Promise<{bet: number, balance: number, token: string, currency: string, game: string, bets: number[], winningLines: number[][], paytable: Object}>}
   * @throws {Error} If window.__OPTIONS__ is missing or required fields are absent
   * @throws {Error} If API call fails (network error or API error response)
   */
  async initGame() {
    // Task 1: Extract window.__OPTIONS__ configuration
    if (!window.__OPTIONS__) {
      throw new Error(
        "PopiPlay: window.__OPTIONS__ not found. Game must be loaded from PopiPlay server."
      );
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    // Validate required fields
    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(
          ", "
        )}]`
      );
    }

    try {
      // CSRF header handling: Provider requires CSRF tokens for all API calls
      // CSRF header name and value are provided dynamically via window.__OPTIONS__
      // Header name is dynamic (e.g., "X-CSRF-Token") and value is a dynamic token
      // Both are included in the request headers using computed property name syntax
      const response = await axios.post(
        apiUrl, // API URL includes play_token in path (from window.__OPTIONS__.api)
        { command: "init" },
        {
          headers: {
            [csrfHeaderName]: csrfHeaderValue, // Dynamic CSRF header: header name and value from window.__OPTIONS__
            "Content-Type": "application/json",
          },
        }
      );

      // API version validation: Provider integration supports API version 2 only
      // This helps identify API version mismatches during development
      if (response.data?.api_version && response.data.api_version !== "2") {
        // API version validation - no logging in production
      }

      // Task 6: Catch invalid response structure
      if (!response.data) {
        throw new Error(
          "PopiPlay: Invalid API response - response.data is missing"
        );
      }

      // Task 6: Catch API error responses
      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(
          `PopiPlay: API error: ${error.desc} (code: ${error.code})`
        );
      }

      // Task 3: Parse init response and extract required fields
      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const screen = response.data?.options?.screen;
      const featureMultipliers = response.data?.options?.feature_options?.feature_multipliers;

      // Validate response structure before accessing nested properties
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error(
          "PopiPlay: Invalid API response - options.available_bets is missing or invalid"
        );
      }
      if (typeof defaultBet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - options.default_bet is missing or invalid"
        );
      }
      if (typeof baseBet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - options.base_bet is missing or invalid"
        );
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error(
          "PopiPlay: Invalid API response - options.paytable is missing or invalid"
        );
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error(
          "PopiPlay: Invalid API response - options.lines is missing or invalid"
        );
      }
      if (typeof wallet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - balance.wallet is missing or invalid"
        );
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error(
          "PopiPlay: Invalid API response - options.currency.code is missing or invalid"
        );
      }

      // Calculate bonus buy multiplier if feature_multipliers present
      const bonusBuyMultiplier =
        featureMultipliers?.base_bet && featureMultipliers?.bonus_buy
          ? featureMultipliers.bonus_buy / featureMultipliers.base_bet
          : undefined;

      // Extract currency configuration for dynamic formatting
      // Currency object contains: { code, symbol, subunits, exponent }
      // - subunits: number of subunits per main unit (e.g., 100 for cents/dollars)
      // - exponent: number of decimal places to display (e.g., 2 for "10.00")
      // - symbol: currency symbol for display (e.g., "$", "€", "FUN")
      const currencyConfig = {
        subunits: currencyObj.subunits || 100, // Default to 100 if not provided
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2, // Default to 2 if not provided
        symbol: currencyObj.symbol || currencyObj.code || "", // Fallback to code if symbol not provided
      };

      // Transform response to match standard return format
      // Token decision: Use play_token for initGame() (session token, UUID v4, no expiration)
      // Currency comes from API response (options.currency.code), NOT window.__OPTIONS__.currency
      const result = {
        bet: defaultBet,
        baseBet: baseBet,
        balance: wallet, // Extracted from balance.wallet (nested value access)
        token: playToken, // Using play_token as session identifier (UUID v4, no expiration)
        currency: currencyObj.code, // From API response, NOT window.__OPTIONS__.currency
        currencyConfig: currencyConfig, // Currency formatting configuration
        game: identifier, // From window.__OPTIONS__.identifier
        bets: availableBets,
        winningLines: lines,
        paytable: paytable,
        bonusBuyMultiplier: bonusBuyMultiplier,
        availableActions: response.data?.flow?.available_actions || [
          "init",
          "spin",
        ], // Track available commands
        flowState: response.data?.flow?.state || "ready", // Track game state machine
        features: response.data?.features || null, // Include features for Hold and Win reload detection
        command: "init", // Track that this was an init command
      };


      // CRITICAL: Handle Hold and Win reload state FIRST
      // If flowState is "respin", we're reloading into an active Hold and Win session
      const isHoldAndWinReload =
        result.flowState === "respin" &&
        result.availableActions?.includes("respin") &&
        result.features?.respins_left !== undefined;

      if (isHoldAndWinReload) {
        // Extract outcome data (current Hold and Win screen state)
        const outcome = response.data?.outcome;

        // Check if screen has regular symbols (non-coin, non-blank)
        // If yes: reload right after trigger (before first respin) - show intro dialog
        // If no: reload mid-feature (after at least one respin) - show reload dialog
        const hasRegularSymbolsInScreen = hasRegularSymbols(outcome?.screen);

        if (outcome) {
          if (outcome.screen && Array.isArray(outcome.screen)) {
            const normalizedCurrentScreen = normalizeScreenData(outcome.screen);

            // CRITICAL: For ANY Hold and Win reload, use trigger_screen from features for base game initialReels
            // This is the original trigger screen (regular symbols + coins) that player will see when returning to base game
            const triggerScreen = result.features?.trigger_screen;
            const normalizedTriggerScreen = triggerScreen && Array.isArray(triggerScreen)
              ? normalizeScreenData(triggerScreen)
              : normalizedCurrentScreen; // Fallback to current screen if trigger_screen not available

            result.initialReels = normalizedTriggerScreen;

            if (hasRegularSymbolsInScreen) {
              // Reload right after trigger (before first respin)
              // Set up for showing intro dialog with base game
              result.isHoldAndWinIntroReload = true;

              // CRITICAL: Transform current screen for Hold and Win rendering (coins + blanks)
              const transformedReels = transformForHoldAndWin(normalizedCurrentScreen);

              // CRITICAL: For intro reload, base game should render transformed reels (coins + blanks)
              // This prevents flash of regular symbols before scene transition
              // The trigger screen will be used later when returning to base game after Hold and Win completes
              result.round = {
                reels: transformedReels,
                wins: outcome.wins || [],
                win: outcome.win || 0,
                bet: outcome.bet || defaultBet,
              };

              result.holdAndWinInitialReels = transformedReels;
              result.holdAndWinRound = {
                reels: transformedReels,
                wins: outcome.wins || [],
                win: outcome.win || 0,
                bet: outcome.bet || defaultBet,
                storage: outcome.storage || null,
              };
            } else {
              // Reload mid-feature (after at least one respin)
              // Set up for showing reload dialog with Hold and Win game
              result.isHoldAndWinMidFeatureReload = true;

              // Set as Hold and Win initial reels (current state with blanks and coins)
              result.holdAndWinInitialReels = normalizedCurrentScreen;

              // Also create a round object for featureGameSettingsSlice
              result.holdAndWinRound = {
                reels: normalizedCurrentScreen,
                wins: outcome.wins || [],
                win: outcome.win || 0,
                bet: outcome.bet || defaultBet,
                storage: outcome.storage || null,
              };
            }
          }

          // Extract storage data (coin_multipliers, progress_bar, etc.)
          if (outcome.storage) {
            result.holdAndWinStorage = outcome.storage;
          }
        }
      } else {
        // Normal init (not Hold and Win reload)
        // Include normalized reels if screen data is present in init response
        if (screen && Array.isArray(screen)) {
          // Normalize screen symbols from strings to numbers (no buffers needed)
          const normalizedScreen = screen.map((reel) => {
            if (!Array.isArray(reel)) {
              return reel;
            }
            return reel.map((symbolValue) => {
              return typeof symbolValue === "string"
                ? parseInt(symbolValue, 10)
                : symbolValue;
            });
          });

          // Just use the normalized screen data as-is (no padding needed)
          result.initialReels = normalizedScreen;
        }
      }

      return result;
    } catch (error) {
      // Task 6: Implement comprehensive error handling
      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        const apiError = error.response.data.errors[0];
        const errorMessage = `PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`;
        throw new Error(errorMessage);
      }

      if (error.message && error.message.startsWith("PopiPlay:")) {
        // Already a PopiPlay error, just rethrow
        throw error;
      }

      // Network or other axios errors
      const errorMessage = `PopiPlay: Init API call failed: ${error.message}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Execute spin via API using Web Worker
   *
   * Creates a Promise wrapper around spin worker, sends bet and token to worker,
   * transforms response and updates token for subsequent requests.
   * Uses lazy worker initialization - worker is created on first spin() call and reused.
   *
   * Token handling:
   * - First spin: Uses play_token from window.__OPTIONS__ (session token)
   * - Subsequent spins: Uses flow.round_id from previous spin response (round identifier)
   * - Token rotates with each spin (flow.round_id), unlike initGame() which uses play_token
   *
   * Command handling:
   * - Regular game: Uses "spin" command
   * - Feature game: Uses "respin" command (determined by available_actions from API)
   *
   * @param {number} bet - Bet amount in subunits
   * @param {number|string} [token] - Optional token (play_token for first spin, flow.round_id for subsequent spins). If not provided, uses this.currentToken or options.play_token
   * @param {string} command - Command to send to API ("spin" or "respin") - determined by availableActions
   * @param {string} [purchasedFeature] - Optional purchased feature (e.g., "bonus_buy") to include in options
   * @returns {Promise<{type: string, balance: number, round: Object, token: number|string, bet: number, win: number, availableActions: string[], flowState: string}>}
   * @throws {Error} If window.__OPTIONS__ is missing or required fields are absent
   * @throws {Error} If worker error occurs
   * @throws {Error} If API error occurs
   * @throws {Error} If response transformation fails
   */
  spin(bet, token, command, purchasedFeature) {
    return new Promise((resolve, reject) => {
      // Worker lifecycle: Lazy initialization - worker created on first spin() call and reused
      // Worker instance is stored in this.worker and reused for all subsequent spins
      // Worker termination handled by browser when page unloads
      // Memory management: Single worker instance per provider instance
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode); // Create inline worker from spinWorkerCode string
      }

      // Task 3: Extract window.__OPTIONS__ values
      const options = window.__OPTIONS__;
      if (!options) {
        reject(
          new Error(
            "PopiPlay: window.__OPTIONS__ not found. Game must be loaded from PopiPlay server."
          )
        );
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      // Task 3: Validate required fields
      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(
          new Error(
            `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(
              ", "
            )}]`
          )
        );
        return;
      }

      // Set up worker message handler
      // Worker sends response in format: { balance, round, token, bet } (after extracting nested values)
      this.worker.onmessage = (event) => {
        const data = event.data;

        // Check for error in worker response
        if (data.error) {
          // If error is an object with errors array (API error), reject with full object
          // Otherwise reject with Error object containing the error message
          if (typeof data.error === 'object' && data.error.errors) {
            reject(data.error); // Reject with full error object
          } else {
            reject(new Error(data.error)); // Reject with Error object
          }
          return;
        }

        // Provider-specific transformation: Normalize screen symbols to numbers
        const transformedData = this.transformReelsSymbols(data);

        // Token rotation pattern: Update token for subsequent requests
        // First spin: Uses play_token from window.__OPTIONS__
        // Subsequent spins: Uses flow.round_id from previous spin response (round identifier)
        // Extract token from API response (flow.round_id)
        if (transformedData.flow && transformedData.flow.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        // Extract balance.wallet if balance is an object (provider API format)
        let extractedBalance = transformedData.balance;
        if (
          transformedData.balance &&
          typeof transformedData.balance === "object" &&
          transformedData.balance.wallet !== undefined
        ) {
          extractedBalance = transformedData.balance.wallet;
        }

        // Normalize response structure for Redux
        // Redux slice expects: { round, token, bet, balance, availableActions, flowState }
        // Provider API returns: { outcome, flow, balance, features }

        // Apply win transformation using getResponseHandler logic
        // This transforms provider's win format from tuples to objects
        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(
            transformedData.outcome
          );
        }

        // Handle hold and win feature game rounds
        const isHoldAndWinTrigger =
          command === "spin" && transformedData.flow?.state === "respin";
        const isRespin = command === "respin";

        // PopiPlay-specific balance calculation:
        // API balance doesn't include win until next request, so we add it client-side
        // This ensures balance updates immediately after wins are displayed
        const winAmount = normalizedOutcome?.win || 0;
        const balanceWithWin = extractedBalance + winAmount;

        const result = {
          token: transformedData.flow?.round_id || null, // Extract token from flow.round_id
          bet: transformedData.outcome?.bet || null, // Extract bet from outcome
          balance: balanceWithWin, // Use balance + win (PopiPlay-specific calculation)
          availableActions: transformedData.flow?.available_actions || [
            "init",
            "spin",
          ],
          flowState: transformedData.flow?.state || "ready",
          // Include features for respin tracking (respins_issued, respins_left)
          features: transformedData.features || null,
          command: command, // Add command that was sent to API
        };

        // CRITICAL: For respin commands, do NOT update popiGameSettingsSlice.js
        // Only update featureGameSettingsSlice.js with holdAndWinRound
        if (isRespin) {
          // Respin: Only update feature game state, NOT main game state
          const originalScreen = data.outcome?.screen; // Use raw data, not transformed
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            // Use original screen data as-is, just normalize string to numbers
            respinRound.reels = originalScreen.map((reel) => {
              if (!Array.isArray(reel)) return reel;
              return reel.map((symbolId) => {
                // Normalize string to number if needed
                return typeof symbolId === "string"
                  ? parseInt(symbolId, 10)
                  : symbolId;
              });
            });
            result.holdAndWinRound = respinRound;
          }
          // Do NOT set result.round - this prevents updating popiGameSettingsSlice.js
        } else if (isHoldAndWinTrigger) {
          // Trigger round: Update main game state with ORIGINAL reels (with buffers)
          result.round = normalizedOutcome; // Original reels with buffers for main game

          // CRITICAL: Backend sends screen with coins + regular symbols on trigger
          // We need to transform it to coins + blanks (0) for Hold and Win rendering
          const originalScreen = data.outcome?.screen; // Use raw data, not transformed
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            const transformedReels = transformForHoldAndWin(normalizedReels);

            // Send transformed reels for Hold and Win rendering
            result.holdAndWinInitialReels = transformedReels;

            // Also send as current round so controllers initialize with correct symbolId
            const transformedRound = { ...normalizedOutcome };
            transformedRound.reels = transformedReels;
            result.holdAndWinRound = transformedRound;
          }
        } else {
          // Regular spin: Update main game state normally
          result.round = normalizedOutcome; // Use transformed outcome with normalized wins
        }

        // Return transformed data (with normalized reels and flow info)
        // Redux slice will call processRGSResponse() to normalize the response
        resolve(result);
      };

      // Task 9: Set up worker error handler
      this.worker.onerror = (event) => {
        const errorMessage = `PopiPlay: Worker error: ${
          event.message || "Unknown worker error"
        }`;
        reject(new Error(errorMessage));
      };

      // Send message to worker with required data
      // Token fallback logic:
      //   1. Use explicit token parameter if provided
      //   2. Use this.currentToken (flow.round_id from previous spin) if available
      //   3. Fall back to play_token from window.__OPTIONS__ (for first spin)
      // API URL structure: apiUrl always contains play_token in path (same URL used for all spins)
      // Token parameter changes: play_token for first spin, flow.round_id for subsequent spins
      // Command parameter: "spin" for regular game, "respin" for feature game

      const tokenToUse = token || this.currentToken || playToken;

      this.worker.postMessage({
        bet,
        token: tokenToUse, // Token rotation: play_token → flow.round_id
        apiUrl, // Full URL including play_token in path - same URL used for all spins
        csrfTokenName: csrfTokenHeaderName, // CSRF header name (dynamic, from window.__OPTIONS__)
        csrfTokenValue: csrfTokenHeaderValue, // CSRF header value (dynamic, from window.__OPTIONS__)
        identifier, // For reference, not used in API call
        command: command, // Command to send: "spin" or "respin" (based on available_actions)
        ...(purchasedFeature ? { purchasedFeature } : {}), // Add purchasedFeature if provided
      });
    });
  }

  /**
   * Simulate spin with predetermined outcome
   *
   * @param {array} slotface - Predetermined symbols
   * @param {number} bet - Bet amount in subunits
   * @param {string} token - Session token (play_token)
   * @returns {Promise<{token, round, bet, balance}>}
   * @throws {Error} "simulate() must be implemented"
   */
  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }
}

// Export spinWorkerCode for testing purposes
export { spinWorkerCode };

export default PopiPlayRGS;
